import { Link } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';
import { useState } from 'react';

// ✏️ Pour modifier la sélection des nouveautés, éditez simplement cette liste.
// Chaque entrée = un produit affiché dans la section "Nouveautés".
const NEW_PRODUCTS = [
  {
    id: 'herisson-porte-lettres-livre-plie',
    name: 'Hérisson porte-lettres en livre plié (pièce unique)',
    price: 30,
    image: 'https://storage.readdy-site.link/project_files/fbdeed4b-ebea-4a2a-94ca-d34ec0e1d4bd/cc708602-b8d9-473a-a5fd-ad64f3c89a7c_herisson-porte-lettre.png?v=8a15e9c968ee51dc9ca42398efc2e833',
    description: 'Un petit hérisson en livre plié qui garde joliment votre courrier : une création artisanale utile, poétique et pleine de charme.',
    link: '/produit-herisson-porte-lettres-livre-plie',
    badge: 'Pièce unique ✨',
    badgeColor: 'bg-[#C49B86] text-white',
  },
  {
    id: 'bouquet-eternel-livre-plie',
    name: 'Livre plié « Bouquet éternel » (pièce unique)',
    price: 30,
    image: 'https://storage.readdy-site.link/project_files/fbdeed4b-ebea-4a2a-94ca-d34ec0e1d4bd/74813c06-f7b3-4490-891c-e6d4bd3b29e7_Vase-en-livre-avec-roses.png?v=02ad9ca1b9be210449a60e78a21cb4b5',
    description: 'Un livre transformé en vase poétique, habillé d\'un bouquet de roses rouges et ivoire. Une pièce unique, douce et lumineuse.',
    link: '/produit-bouquet-eternel-livre-plie',
    badge: 'Pièce unique ✨',
    badgeColor: 'bg-[#C49B86] text-white',
  },
  {
    id: 'belier-livre-plie',
    name: 'Bélier \u2648\uFE0E – Livre plié (pièce unique)',
    price: 30,
    image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/ef11425d0704a77c29a3ec9594799de9.png',
    description: 'Le symbole ♈︎ du Bélier sculpté dans les pages d\'un livre. Une pièce unique, minimaliste et élégante.',
    link: '/produit-belier-livre-plie',
    badge: 'Pièce unique',
    badgeColor: 'bg-[#C49B86] text-white',
  },
];

interface NewArrivalsSectionProps {
  /** Afficher le titre de section (true par défaut) */
  showTitle?: boolean;
}

export default function NewArrivalsSection({ showTitle = true }: NewArrivalsSectionProps) {
  const { addItem } = useCart();
  const [addedId, setAddedId] = useState<string | null>(null);

  const handleAddToCart = (product: typeof NEW_PRODUCTS[0]) => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      description: product.description,
    });
    setAddedId(product.id);
    setTimeout(() => setAddedId(null), 1500);
  };

  return (
    <section className="py-12 sm:py-16 bg-[#FAF9F8]" data-product-shop>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {showTitle && (
          <div className="text-center mb-10 sm:mb-14">
            <span
              className="inline-block text-xs sm:text-sm font-semibold tracking-widest uppercase text-[#B97A72] mb-3"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Tout juste arrivé
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#C49B86] mb-4"
              style={{ fontFamily: 'Allura, cursive' }}
            >
              Nouveautés
            </h2>
            <p
              className="text-base sm:text-lg text-[#6B5E5A] max-w-2xl mx-auto"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Mes dernières créations artisanales, faites main avec soin — à découvrir en avant-première.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {NEW_PRODUCTS.map((product) => (
            <article
              key={product.id}
              className="bg-white border-2 border-[#EBD2CF] rounded-lg overflow-hidden hover:border-[#B97A72] transition-all group"
            >
              {/* Image */}
              <div className="aspect-square relative overflow-hidden bg-[#F8EDEA]">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                {/* Badge */}
                {product.badge && (
                  <div className="absolute top-3 left-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${product.badgeColor}`}>
                      {product.badge}
                    </span>
                  </div>
                )}
                {/* Label Nouveauté */}
                <div className="absolute top-3 right-3">
                  <span className="bg-[#B97A72] text-white px-2 py-1 rounded-full text-xs font-semibold tracking-wide">
                    Nouveauté
                  </span>
                </div>
              </div>

              {/* Contenu */}
              <div className="p-4 sm:p-6">
                <h3
                  className="text-base sm:text-lg font-semibold text-[#6B5E5A] mb-2 leading-snug"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {product.name}
                </h3>
                <p
                  className="text-xs sm:text-sm text-[#9B8B87] mb-4 line-clamp-2 italic"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {product.description}
                </p>
                <div className="flex items-center justify-between gap-3">
                  <span
                    className="text-xl sm:text-2xl font-bold text-[#B97A72]"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {product.price}€
                  </span>
                  <div className="flex gap-2">
                    <Link
                      to={product.link}
                      className="bg-transparent text-[#B97A72] px-3 py-2 rounded-full border-2 border-[#B97A72] hover:bg-[#B97A72] hover:text-white transition-colors whitespace-nowrap text-xs sm:text-sm cursor-pointer"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      Voir
                    </Link>
                    <button
                      onClick={() => handleAddToCart(product)}
                      className="bg-[#B97A72] text-white px-3 py-2 rounded-full hover:bg-[#C49B86] transition-colors whitespace-nowrap text-xs sm:text-sm cursor-pointer"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {addedId === product.id ? (
                        <><i className="ri-check-line mr-1"></i>Ajouté !</>
                      ) : (
                        <><i className="ri-shopping-cart-line mr-1"></i>Ajouter</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Bouton CTA */}
        <div className="text-center mt-10 sm:mt-14">
          <Link
            to="/boutique"
            className="inline-block bg-transparent text-[#B97A72] px-8 py-3 rounded-full border-2 border-[#B97A72] hover:bg-[#B97A72] hover:text-white transition-colors whitespace-nowrap cursor-pointer text-base sm:text-lg font-semibold"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Voir toutes les nouveautés
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
